import React, { useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { AskRichardWordmark } from '~/components/brand';

import type { LeDataSessionSummary } from '~/types/leData';

import { RICHARD_MICROCOPY } from './microcopy';

/** Row title falls back to a ~40 char slice of the first question only when no generated name exists. */
export const SESSION_TITLE_PREVIEW_LENGTH = 40;

/** The search field only earns its space once the list is long enough to be worth filtering. */
export const SESSION_SEARCH_THRESHOLD = 10;

// Context instructions are sent alongside a person's question, but older backends can fold that
// hidden text into the generated session title. Keep those implementation details out of every
// surface that consumes deriveSessionRowTitle (rail, history and command palette).
const INTERNAL_CONTEXT_MARKERS = [
  'Analytics domain context:',
  'Analytics metric context:',
  'Dataset context:',
  'Lens context:',
] as const;

export function stripInternalSessionContext(value: string): string {
  const markerIndex = INTERNAL_CONTEXT_MARKERS.reduce((earliest, marker) => {
    const index = value.indexOf(marker);
    return index >= 0 && (earliest < 0 || index < earliest) ? index : earliest;
  }, -1);
  return (markerIndex >= 0 ? value.slice(0, markerIndex) : value).trim();
}

const ONE_MINUTE_MS = 60_000;
const ONE_HOUR_MS = 60 * ONE_MINUTE_MS;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;

/**
 * Row title. Prefer the (backend-generated) conversation name; only when a session has no name do
 * we derive a title from the first question, sliced to a readable preview length. Never re-derives
 * from scratch when a name is already present.
 */
export function deriveSessionRowTitle(session: LeDataSessionSummary): string {
  const title = stripInternalSessionContext(session.title ?? '');
  if (title) {
    return title;
  }
  const question = stripInternalSessionContext(session.question ?? '');
  if (question) {
    return question.length > SESSION_TITLE_PREVIEW_LENGTH
      ? `${question.slice(0, SESSION_TITLE_PREVIEW_LENGTH).trimEnd()}…`
      : question;
  }
  return 'Untitled chat';
}

export type SessionGroupLabel = 'Today' | 'Yesterday' | 'Previous 7 days' | 'Older';

/** Fixed render order for the recency buckets, quietest-context-first. */
export const SESSION_GROUP_ORDER: ReadonlyArray<SessionGroupLabel> = ['Today', 'Yesterday', 'Previous 7 days', 'Older'];

/**
 * Which recency bucket a session's timestamp falls into, relative to `now`. Compares against the
 * start of the local calendar day so "Yesterday" means the previous calendar day, not "24h ago".
 */
export function getSessionGroupLabel(updatedAt: string, now: number): SessionGroupLabel {
  const then = new Date(updatedAt).getTime();
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const startOfTodayMs = startOfToday.getTime();

  if (!Number.isFinite(then) || then >= startOfTodayMs) {
    return 'Today';
  }
  if (then >= startOfTodayMs - ONE_DAY_MS) {
    return 'Yesterday';
  }
  if (then >= startOfTodayMs - 7 * ONE_DAY_MS) {
    return 'Previous 7 days';
  }
  return 'Older';
}

export interface SessionGroup {
  label: SessionGroupLabel;
  sessions: Array<LeDataSessionSummary>;
}

/**
 * Buckets sessions into the recency groups in fixed order, preserving the incoming (recency-sorted)
 * order within each group. Empty groups are dropped so the sidebar never shows a bare header.
 */
export function groupSessionsByRecency(sessions: Array<LeDataSessionSummary>, now: number): Array<SessionGroup> {
  const buckets = new Map<SessionGroupLabel, Array<LeDataSessionSummary>>();

  for (const session of sessions) {
    const label = getSessionGroupLabel(session.updatedAt, now);
    const bucket = buckets.get(label);
    if (bucket) {
      bucket.push(session);
    } else {
      buckets.set(label, [session]);
    }
  }

  return SESSION_GROUP_ORDER.filter((label) => buckets.has(label)).map((label) => ({
    label,
    sessions: buckets.get(label) as Array<LeDataSessionSummary>,
  }));
}

/** Compact relative age shown on row hover: "just now", "5m ago", "2h ago", "3d ago", "2w ago", ... */
export function formatRelativeTime(updatedAt: string, now: number): string {
  const then = new Date(updatedAt).getTime();
  if (!Number.isFinite(then)) {
    return '';
  }
  const diff = now - then;
  if (diff < ONE_MINUTE_MS) {
    return 'just now';
  }
  if (diff < ONE_HOUR_MS) {
    return `${Math.floor(diff / ONE_MINUTE_MS)}m ago`;
  }
  if (diff < ONE_DAY_MS) {
    return `${Math.floor(diff / ONE_HOUR_MS)}h ago`;
  }
  const days = Math.floor(diff / ONE_DAY_MS);
  if (days < 7) {
    return `${days}d ago`;
  }
  if (days < 30) {
    return `${Math.floor(days / 7)}w ago`;
  }
  if (days < 365) {
    return `${Math.floor(days / 30)}mo ago`;
  }
  return `${Math.floor(days / 365)}y ago`;
}

/** The rail needs a denser recency signal than a conversational sentence: "5m", "2h", "3d". */
export function formatCompactRelativeTime(updatedAt: string, now: number): string {
  const relative = formatRelativeTime(updatedAt, now);
  if (relative === 'just now') {
    return 'now';
  }
  return relative.replace(' ago', '');
}

/** Case-insensitive client-side filter over the title and original question. Blank query passes all. */
export function filterSessions(sessions: Array<LeDataSessionSummary>, query: string): Array<LeDataSessionSummary> {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return sessions;
  }
  return sessions.filter((session) => {
    const title = session.title?.toLowerCase() ?? '';
    const question = session.question?.toLowerCase() ?? '';
    return title.includes(needle) || question.includes(needle);
  });
}

interface SessionHistoryPanelProps {
  sessions: Array<LeDataSessionSummary>;
  selectedSessionId: string | null;
  sessionsLoading: boolean;
  sessionsError: boolean;
  sessionsErrorValue: unknown;
  showDeleteActions: boolean;
  /** Session ids known to be org-shared (visibility luxuryescapes_link); rows in this set get a people glyph. */
  sharedSessionIds?: ReadonlySet<string>;
  onStartNewChat: () => void;
  onOpenSession: (sessionId: string) => void;
  onDeleteSession: (session: LeDataSessionSummary) => void;
  /** Injectable clock for deterministic grouping/relative-time in tests; defaults to Date.now(). */
  now?: number;
}

/**
 * The recent-chats index: a quiet, scannable list of conversations rather than an outlined sibling
 * panel. Rows are compact single lines titled by the conversation name, bucketed under quiet
 * Today / Yesterday / Previous 7 days / Older headers, with the relative age and original question
 * surfaced on hover. Rename/delete live on hover (delete is handled by the parent, which offers an
 * undo window), a search field fades in once the list is long, and org-shared sessions carry a
 * people glyph. "Recent" is the only tracked-out label on the surface.
 */
export default function SessionHistoryPanel({
  sessions,
  selectedSessionId,
  sessionsLoading,
  sessionsError,
  sessionsErrorValue,
  showDeleteActions,
  sharedSessionIds,
  onStartNewChat,
  onOpenSession,
  onDeleteSession,
  now,
}: SessionHistoryPanelProps) {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [query, setQuery] = useState('');

  // Snap the live clock to a whole minute so grouping/relative-time memos stay stable across
  // renders (Date.now() every render would defeat the memo). The injected `now` seam is preserved.
  const nowMs = now ?? Math.floor(Date.now() / 60_000) * 60_000;
  const showSearch = sessions.length > SESSION_SEARCH_THRESHOLD;
  const filteredSessions = useMemo(
    () => (showSearch ? filterSessions(sessions, query) : sessions),
    [showSearch, sessions, query],
  );
  const groups = useMemo(() => groupSessionsByRecency(filteredSessions, nowMs), [filteredSessions, nowMs]);

  const hasSessions = sessions.length > 0;
  const noMatches = hasSessions && !sessionsLoading && filteredSessions.length === 0;
  const rowTransition = prefersReducedMotion ? 'none' : 'opacity 120ms ease';

  return (
    <>
      <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
        <Box component="h1" sx={{ display: 'flex', m: 0, mb: 1.5 }}>
          <AskRichardWordmark inverted={isDarkMode} size={20} />
        </Box>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={onStartNewChat}
          sx={{ borderRadius: 1 }}
        >
          New chat
        </Button>
      </Box>

      <Typography
        component="h2"
        sx={{
          px: 2,
          pt: 1.5,
          pb: 0.5,
          fontSize: 11,
          fontWeight: 500,
          textTransform: 'uppercase',
          // The single tracked-out label on the surface; group headers and rows stay untracked.
          letterSpacing: '0.08em',
          color: 'text.secondary',
        }}
      >
        Recent
      </Typography>

      <Collapse in={showSearch} timeout={prefersReducedMotion ? 0 : 200} unmountOnExit>
        <Box sx={{ px: 1.5, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search chats"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              },
              htmlInput: { 'aria-label': 'Search chats' },
            }}
          />
        </Box>
      </Collapse>

      <List dense sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 0 }}>
        {sessionsLoading && !hasSessions ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        ) : null}
        {sessionsError && !hasSessions ? (
          <Typography variant="body2" color="error" sx={{ px: 2, py: 1 }}>
            {sessionsErrorValue instanceof Error ? sessionsErrorValue.message : 'Failed to load recent sessions.'}
          </Typography>
        ) : null}
        {!sessionsLoading && !sessionsError && !hasSessions ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
            {RICHARD_MICROCOPY.emptySidebar}
          </Typography>
        ) : null}
        {noMatches ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
            No chats match your search.
          </Typography>
        ) : null}

        {groups.map((group) => (
          <React.Fragment key={group.label}>
            <Box component="li" sx={{ listStyle: 'none', px: 2, pt: 1.25, pb: 0.25 }}>
              <Typography component="span" sx={{ fontSize: 11, fontWeight: 500, color: 'text.disabled' }}>
                {group.label}
              </Typography>
            </Box>
            {group.sessions.map((session) => {
              const title = deriveSessionRowTitle(session);
              const relative = formatRelativeTime(session.updatedAt, nowMs);
              const question = session.question?.trim();
              const distinctQuestion = question && question !== session.title ? question : undefined;
              const hoverText = distinctQuestion ? `${distinctQuestion} · ${relative}` : relative;
              const isShared = sharedSessionIds?.has(session.sessionId) ?? false;

              return (
                <ListItem
                  key={session.sessionId}
                  disablePadding
                  sx={{
                    px: 0.75,
                    '& .session-row-delete': { opacity: 0, transition: rowTransition },
                    '& .session-row-shared': { transition: rowTransition },
                    '&:hover .session-row-delete, &:focus-within .session-row-delete': { opacity: 1 },
                    // The people glyph steps aside for the delete action while the row is engaged.
                    '&:hover .session-row-shared, &:focus-within .session-row-shared': { opacity: 0 },
                  }}
                  secondaryAction={
                    showDeleteActions ? (
                      <IconButton
                        className="session-row-delete"
                        edge="end"
                        aria-label={`Delete ${title}`}
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          onDeleteSession(session);
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    ) : undefined
                  }
                >
                  <Tooltip title={hoverText} placement="right" enterDelay={200}>
                    <ListItemButton
                      selected={selectedSessionId === session.sessionId}
                      onClick={() => onOpenSession(session.sessionId)}
                      sx={{
                        minHeight: 36,
                        borderRadius: '6px',
                        py: 0.25,
                        pr: 5,
                      }}
                    >
                      <ListItemText primary={title} primaryTypographyProps={{ noWrap: true, variant: 'body2' }} />
                      {isShared ? (
                        <PeopleAltOutlinedIcon
                          className="session-row-shared"
                          role="img"
                          aria-label="Shared with your organisation"
                          sx={{ fontSize: 16, color: 'text.disabled', ml: 0.5, flexShrink: 0 }}
                        />
                      ) : null}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              );
            })}
          </React.Fragment>
        ))}
      </List>
    </>
  );
}
